import { __assign, __read } from "tslib";
import { useCallback, useEffect, useReducer, useRef, } from 'react';
import { propsUpdate } from './actions';
import { extractTimeParts, isHourType, isMinuteType, isPotentialTimeString, isTimeComplete, isValidHour, isValidMinute, timeNumberToString, timeToString, } from '../utils';
import { assertNever } from '../../../utils/ts-assert-never';
/* Think about getting rid of the reducer style for the state,
 *  It became quite complex. See if it can be more straight forward. */
export function useTimeselectHooks(props, compositeBlur) {
    /* To be able to compare old and new props */
    var prevPropsRef = useRef(props);
    /* To be able to compare old and new time */
    var prevOnChangeTime = useRef(undefined);
    /* (not used right now) Could be handy to prevent/do certain action
     * after a specific action. */
    var actionsRef = useRef([]);
    /* Our wrapped reducer, to be able to add the actions to a list
     * and add the number lists (hour/minute), needed in the reducer. */
    var reducer = useCallback(function (_state, _action) {
        actionsRef.current.push(_action);
        return stateReducer(_state, _action);
    }, []);
    /* Our state & dispatch function. */
    var _a = __read(useReducer(reducer, props, reducerStateInit), 2), state = _a[0], dispatch = _a[1];
    var externalChangeRef = useRef(true);
    /* To update state based on new props coming in. */
    useEffect(function () {
        if (prevPropsRef.current.value !== props.value) {
            // todo maybe instead of prevOnChangeTime, we can have a copy of the prevState,
            // this here below relies on the same extraction of time as in the state, not
            // so good. Maybe we can also remove the action tracker then. (no time now)
            prevOnChangeTime.current = extractTimeParts(props.value || '');
            dispatch(propsUpdate(props));
            externalChangeRef.current = true;
        }
    }, [props.value]);
    /* Calling onChange if time is different after update,
     * and selector is closed. Would like to make it more simple,
     * if possible take it out of this hook. */
    useEffect(function () {
        var _a;
        var timeObj = {
            hour: state.selectedHour,
            minute: state.selectedMinute,
        };
        if (!externalChangeRef.current &&
            shouldCallOnChange(prevOnChangeTime.current, timeObj, state, actionsRef.current)) {
            var timeIsComplete = isTimeComplete(timeObj);
            prevOnChangeTime.current = timeObj;
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, __assign({ value: timeIsComplete ? timeToString(timeObj) : state.inputValue, name: props.name, valid: timeIsComplete }, timeObj));
        }
    }, [state, props.onChange, props.name, actionsRef.current]);
    /* Trigger a blur event manually if we close the selector programmatically after clicking
     * a minute => selection done. */
    useEffect(function () {
        if (containsAction('SelectMinute', actionsRef.current) &&
            containsCloseSelectorAction(actionsRef.current)) {
            compositeBlur();
        }
    }, [compositeBlur, props.name, actionsRef.current]);
    /* Keep these as last effects. */
    /* - Update prevProps to new ones. */
    useEffect(function () {
        prevPropsRef.current = props;
    }, [props]);
    /* - Erase list of actions dispatched. */
    useEffect(function () {
        actionsRef.current = [];
    });
    useEffect(function () {
        externalChangeRef.current = false;
    });
    return { state: state, dispatch: dispatch };
}
function reducerStateInit(props) {
    var timeObj = extractTimeParts(props.value || '') || {
        hour: undefined,
        minute: undefined,
    };
    return {
        currentSelectorTimeType: props.initialOpen ? 'hour' : undefined,
        highlightedHour: timeObj.hour,
        highlightedMinute: timeObj.minute,
        selectedHour: timeObj.hour,
        selectedMinute: timeObj.minute,
        inputValue: props.value || '',
    };
}
function stateReducer(state, action) {
    var changes = {};
    switch (action.type) {
        case 'SelectTimeType': {
            changes = {
                currentSelectorTimeType: action.timeType,
            };
            break;
        }
        case 'HighlightNumber': {
            if (isHourType(state.currentSelectorTimeType)) {
                changes = { highlightedHour: action.number };
            }
            else if (isMinuteType(state.currentSelectorTimeType)) {
                changes = { highlightedMinute: action.number };
            }
            break;
        }
        case 'SelectHour':
            changes = {
                selectedHour: action.hour,
                highlightedHour: action.hour,
                inputValue: timeToString({
                    hour: action.hour,
                    minute: state.selectedMinute,
                }),
            };
            break;
        case 'SelectMinute':
            changes = {
                selectedMinute: action.minute,
                highlightedMinute: action.minute,
                inputValue: timeToString({
                    hour: state.selectedHour,
                    minute: action.minute,
                }),
            };
            break;
        case 'InputChange':
            changes = getInputChangeStateChanges(state.inputValue, action.value);
            break;
        case 'PropsUpdate':
            var timeObj = extractTimeParts(action.props.value || '');
            changes = {
                highlightedHour: timeObj === null || timeObj === void 0 ? void 0 : timeObj.hour,
                highlightedMinute: timeObj === null || timeObj === void 0 ? void 0 : timeObj.minute,
                selectedHour: timeObj === null || timeObj === void 0 ? void 0 : timeObj.hour,
                selectedMinute: timeObj === null || timeObj === void 0 ? void 0 : timeObj.minute,
                inputValue: action.props.value,
            };
            break;
        default:
            // Typescript exhaustive check
            assertNever(action);
    }
    return __assign(__assign({}, state), changes);
}
function getInputChangeStateChanges(oldValue, newValue) {
    var backspaced = oldValue.length > newValue.length;
    if (!isPotentialTimeString(newValue)) {
        return backspaced ? { inputValue: newValue } : {};
    }
    var _a = extractTimeParts(newValue) || {}, hour = _a.hour, minute = _a.minute;
    var _b = __read(oldValue.split(':'), 2), _c = _b[0], oldHourStr = _c === void 0 ? '' : _c, _d = _b[1], oldMinuteStr = _d === void 0 ? '' : _d;
    var _e = __read(newValue.split(':'), 2), _f = _e[0], newHourStr = _f === void 0 ? '' : _f, _g = _e[1], newMinuteStr = _g === void 0 ? '' : _g;
    var hasColon = newValue.indexOf(':') > -1;
    var hourChanged = oldHourStr !== newHourStr;
    var minChanged = oldMinuteStr !== newMinuteStr;
    if (!backspaced &&
        ((hourChanged && !isValidHour(hour)) ||
            (minChanged && !isValidMinute(minute)))) {
        return {};
    }
    var changes = {
        inputValue: (!backspaced && hourChanged && newHourStr.length === 2
            ? timeNumberToString(hour)
            : newHourStr) +
            (hasColon || (!backspaced && hourChanged && newHourStr.length === 2)
                ? ':'
                : '') +
            (newMinuteStr || ''),
    };
    var validHourChange = hourChanged && (hour === undefined || hour < 24);
    var validMinuteChange = minChanged && (minute === undefined || minute < 60);
    if (validHourChange) {
        changes.selectedHour = hour;
        changes.highlightedHour = hour;
    }
    if (validMinuteChange) {
        changes.selectedMinute = minute;
        changes.highlightedMinute = minute;
    }
    // copy/paste
    if (validHourChange && validMinuteChange) {
        changes.currentSelectorTimeType = undefined;
    }
    else if (validHourChange) {
        changes.currentSelectorTimeType =
            hourChanged && newHourStr.length === 2 ? 'minute' : 'hour';
    }
    else if (validMinuteChange) {
        changes.currentSelectorTimeType = 'minute';
        if (newMinuteStr && newMinuteStr.length === 2) {
            changes.currentSelectorTimeType = undefined;
        }
    }
    return changes;
}
function timeDiffers(prevTime, time) {
    return (prevTime === null || prevTime === void 0 ? void 0 : prevTime.hour) !== (time === null || time === void 0 ? void 0 : time.hour) || (prevTime === null || prevTime === void 0 ? void 0 : prevTime.minute) !== (time === null || time === void 0 ? void 0 : time.minute);
}
function shouldCallOnChange(prevOnChangeTime, newTime, state, actions) {
    return ((!state.currentSelectorTimeType || containsCloseSelectorAction(actions)) &&
        timeDiffers(prevOnChangeTime, newTime));
}
function containsAction(actionType, actions) {
    return actions.some(function (action) { return action.type === actionType; });
}
function containsCloseSelectorAction(actions) {
    return actions.some(function (action) {
        return action.type === 'SelectTimeType' && action.timeType === undefined;
    });
}
//# sourceMappingURL=useTimeselectHooks.js.map